import React, { useCallback, useState } from 'react';
import CategoryIcon from './CategoryIcon.jsx';
import { CATEGORY_TINT as TINT } from '../util/categoryTint.js';

// "What are you interested in?" — the web mirror of the iOS onboarding interest
// sheet (`SectionInterestView` + `SectionPreferencePicker`, Daily Six sections
// rework Part 3d).
//
// Tom 2026-07-28: on web this must run BEFORE a non-challenged player is shown
// any back-section questions. A category-challenged arrival never sees it —
// their friend already picked the arena, and asking someone to choose interests
// thirty seconds after being handed a specific challenge would undercut it.
//
// Saved value is `sectionPreferences` on the user doc: the SELECTED categories
// in PRIORITY ORDER, top pick first. Already on the firestore.rules update
// allowlist (line 250), so this needs no rules change — but it does mean the
// write must send exactly that shape and nothing else (root rule 9: one stray
// field rejects the whole atomic write).
//
// ONE DELIBERATE PLATFORM DIFFERENCE: iOS ranks by drag-to-reorder, using the
// native List `.onMove` with a forced-active EditMode (a hand-rolled drag felt
// jittery — Tom 2026-07-24). The web has no equivalent primitive and no drag
// dependency, and app-web is overwhelmingly mobile, where HTML5 drag-and-drop
// is genuinely bad. So here PRIORITY IS TAP ORDER: the first category you tap
// is rank 1, shown in a numbered badge. Same stored semantic, same "top pick
// gets offered first" rule, without a drag that would feel broken on a phone.

const CATEGORIES = ['Sports', 'Markets', 'Politics', 'Culture', 'Discovery', 'World'];

/**
 * @param {string[]} initial   previously-saved sectionPreferences (priority order)
 * @param {(prefs: string[]) => void} onSave  fired on every change (auto-persist, as iOS does)
 * @param {() => void} onDone  Continue / Skip — both simply dismiss, because the
 *                             value is already saved by the time they're tapped
 */
export default function SectionInterestPicker({ initial = [], onSave, onDone }) {
  // `selected` IS the priority list — order carries the ranking, so this is an
  // array rather than a Set.
  const [selected, setSelected] = useState(() =>
    Array.isArray(initial) ? initial.filter((c) => CATEGORIES.includes(c)) : []
  );

  const toggle = useCallback(
    (category) => {
      setSelected((prev) => {
        const next = prev.includes(category)
          ? prev.filter((c) => c !== category)
          : [...prev, category];
        // Persist on every tap, exactly like the iOS picker — so a user who
        // taps two categories and closes the tab has still saved them.
        onSave?.(next);
        return next;
      });
    },
    [onSave]
  );

  return (
    <div style={{ padding: '4px 0' }}>
      <div className="ps-center" style={{ padding: '0 8px 14px' }}>
        <NewspaperIcon />
        <h2 style={{ margin: '10px 0 6px', fontSize: 22, fontWeight: 800 }}>
          What are you interested in?
        </h2>
        <p className="ps-muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
          Pick the sections you want to follow. You can change this anytime.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {CATEGORIES.map((category) => {
          const rank = selected.indexOf(category);
          const isOn = rank >= 0;
          const tint = TINT[category];
          return (
            <button
              key={category}
              type="button"
              onClick={() => toggle(category)}
              aria-pressed={isOn}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                minHeight: 58,
                padding: '0 16px',
                borderRadius: 14,
                border: `1.5px solid ${isOn ? tint : 'var(--border)'}`,
                background: isOn ? tint : 'var(--card-bg)',
                // White on the filled tint, normal ink otherwise. The numbered
                // badge — not colour — is what actually communicates rank, so
                // this reads correctly without relying on hue.
                color: isOn ? '#ffffff' : 'var(--primary-text)',
                fontSize: 16,
                fontWeight: 700,
                textAlign: 'left',
                transition: 'background 160ms, border-color 160ms',
              }}
            >
              <span style={{ display: 'flex', width: 26, justifyContent: 'center' }}>
                <CategoryIcon category={category} size={21} />
              </span>
              <span style={{ flex: 1 }}>{category}</span>
              {isOn && (
                <span
                  aria-label={`priority ${rank + 1}`}
                  style={{
                    minWidth: 24,
                    height: 24,
                    borderRadius: 12,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.28)',
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 800,
                  }}
                >
                  {rank + 1}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <p
        className="ps-muted ps-center"
        style={{ fontSize: 12.5, margin: '12px 0 0', lineHeight: 1.4 }}
      >
        Tap in order — your first pick gets offered first.
      </p>

      <button
        type="button"
        onClick={onDone}
        disabled={selected.length === 0}
        style={{
          width: '100%',
          marginTop: 16,
          padding: '15px 0',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: selected.length > 0 ? 'var(--gold)' : 'var(--surface)',
          color: selected.length > 0 ? '#1a1a1a' : 'var(--secondary-text)',
          fontWeight: 800,
          fontSize: 16,
          cursor: selected.length > 0 ? 'pointer' : 'default',
        }}
      >
        Continue
      </button>
      <button
        type="button"
        onClick={onDone}
        style={{
          width: '100%',
          marginTop: 10,
          padding: '6px 0',
          border: 'none',
          background: 'transparent',
          color: 'var(--secondary-text)',
          fontWeight: 600,
          fontSize: 14.5,
        }}
      >
        Skip for now
      </button>
    </div>
  );
}

// newspaper.fill — the section system's mark on both platforms.
function NewspaperIcon() {
  return (
    <svg
      width={32}
      height={32}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--gold)"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 5h12v14H4z" />
      <path d="M16 8h4v9a2 2 0 0 1-2 2h-2" />
      <path d="M7 8h6M7 11h6M7 14h6" />
    </svg>
  );
}
